import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

type ImportError = { row: number; column?: string; value?: string; message: string };

type Preview = {
  kind: string;
  filename: string;
  encoding: string;
  delimiter: string;
  totalRows: number;
  rowsOk: number;
  rowsError: number;
  errors: ImportError[];
  warnings: { row?: number; message: string }[];
  mapping: { matched: Record<string, string>; ignored: string[] };
  sample: Record<string, unknown>[];
  lineCounts?: Record<string, number>;
  canApply: boolean;
};

type Batch = {
  id: number;
  kind: string;
  filename: string;
  rowsOk: number;
  rowsError: number;
  createdAt: string;
  by: string;
};

/** multipart : on ne passe pas par api(), qui impose du JSON. */
async function postFile<T>(path: string, kind: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const res = await fetch(path, { method: 'POST', body: form, credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? `Erreur ${res.status}`);
  return body as T;
}

export default function Imports() {
  const [kind, setKind] = useState<'CUSTOMERS' | 'PRODUCTS'>('PRODUCTS');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const history = useQuery<{ batches: Batch[] }>({
    queryKey: ['imports'],
    queryFn: async () => (await fetch('/api/admin/imports', { credentials: 'include' })).json(),
  });

  const analyse = useMutation({
    mutationFn: (f: File) => postFile<Preview>('/api/admin/imports/preview', kind, f),
    onSuccess: (data) => {
      setPreview(data);
      setDone(null);
    },
  });

  const commit = useMutation({
    mutationFn: (f: File) => postFile<{ rowsApplied: number }>('/api/admin/imports/commit', kind, f),
    onSuccess: (data) => {
      setDone(`${data.rowsApplied} ligne(s) importée(s).`);
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      void qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  function choose(f: File | null) {
    setFile(f);
    setPreview(null);
    setDone(null);
    if (f) analyse.mutate(f);
  }

  return (
    <div className="space-y-8">
      <section className="bg-card space-y-4 rounded-xl border p-6">
        <h2 className="text-lg font-semibold">Importer un fichier</h2>

        <div className="flex flex-wrap items-end gap-4">
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Type</span>
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as 'CUSTOMERS' | 'PRODUCTS');
                choose(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="border-input bg-background h-11 rounded-lg border px-3"
            >
              <option value="PRODUCTS">Produits</option>
              <option value="CUSTOMERS">Clients</option>
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Fichier CSV</span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => choose(e.target.files?.[0] ?? null)}
              className="border-input h-11 rounded-lg border px-3 py-2 text-sm"
            />
          </label>

          <a
            href={`/api/admin/imports/templates/${kind.toLowerCase()}`}
            className="text-primary h-11 self-end text-sm underline underline-offset-4"
          >
            Télécharger un modèle
          </a>
        </div>

        {analyse.isPending && <p className="text-muted-foreground text-sm">Analyse du fichier…</p>}
        {analyse.isError && (
          <p className="text-destructive text-sm font-medium">{(analyse.error as Error).message}</p>
        )}
        {done && <p className="text-sm font-medium text-green-700 dark:text-green-400">{done}</p>}
      </section>

      {preview && (
        <section className="bg-card space-y-4 rounded-xl border p-6">
          <h2 className="text-lg font-semibold">Aperçu — {preview.filename}</h2>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-muted-foreground">Lignes lues</dt>
            <dd className="font-medium">{preview.totalRows}</dd>
            <dt className="text-muted-foreground">Valides</dt>
            <dd className="font-medium">{preview.rowsOk}</dd>
            <dt className="text-muted-foreground">En erreur</dt>
            <dd className={preview.rowsError ? 'text-destructive font-medium' : 'font-medium'}>
              {preview.rowsError}
            </dd>
            <dt className="text-muted-foreground">Encodage détecté</dt>
            <dd className="font-medium">{preview.encoding}</dd>
          </dl>

          <details className="rounded-lg border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Correspondance des colonnes ({Object.keys(preview.mapping.matched).length} reconnue(s))
            </summary>
            <div className="mt-3 space-y-2 text-sm">
              <ul className="grid gap-1 sm:grid-cols-2">
                {Object.entries(preview.mapping.matched).map(([canonical, source]) => (
                  <li key={canonical} className="font-mono text-xs">
                    <span className="text-muted-foreground">{source}</span> → {canonical}
                  </li>
                ))}
              </ul>
              {preview.mapping.ignored.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  Ignorées : {preview.mapping.ignored.join(', ')}
                </p>
              )}
            </div>
          </details>

          {preview.warnings.length > 0 && (
            <ul className="space-y-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              {preview.warnings.map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </ul>
          )}

          {preview.lineCounts && Object.keys(preview.lineCounts).length > 0 && (
            <p className="text-sm">
              Par ligne :{' '}
              {Object.entries(preview.lineCounts)
                .map(([code, n]) => `${code} (${n})`)
                .join(', ')}
            </p>
          )}

          {preview.rowsError > 0 && (
            <div className="space-y-2">
              <h3 className="text-destructive font-medium">
                Rien ne sera importé tant que ces erreurs subsistent
              </h3>
              <div className="max-h-80 overflow-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Ligne</th>
                      <th className="p-2 text-left font-medium">Colonne</th>
                      <th className="p-2 text-left font-medium">Valeur</th>
                      <th className="p-2 text-left font-medium">Problème</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono">{e.row}</td>
                        <td className="p-2 font-mono">{e.column ?? '—'}</td>
                        <td className="p-2 font-mono">{e.value ?? '—'}</td>
                        <td className="p-2">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {commit.isError && (
            <p className="text-destructive text-sm font-medium">{(commit.error as Error).message}</p>
          )}

          <button
            disabled={!preview.canApply || commit.isPending || !file}
            onClick={() => file && commit.mutate(file)}
            className="bg-primary text-primary-foreground h-12 rounded-lg px-6 font-semibold disabled:opacity-40"
          >
            {commit.isPending ? 'Import en cours…' : `Importer ${preview.rowsOk} ligne(s)`}
          </button>
        </section>
      )}

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-3 text-lg font-semibold">Historique</h2>
        {history.data?.batches.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-left">
                <th className="pb-2 font-medium">Date</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Fichier</th>
                <th className="pb-2 font-medium">OK</th>
                <th className="pb-2 font-medium">Erreurs</th>
                <th className="pb-2 font-medium">Par</th>
              </tr>
            </thead>
            <tbody>
              {history.data.batches.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="py-2">{new Date(b.createdAt).toLocaleString('fr-CA')}</td>
                  <td className="py-2">{b.kind === 'CUSTOMERS' ? 'Clients' : 'Produits'}</td>
                  <td className="py-2 font-mono text-xs">{b.filename}</td>
                  <td className="py-2">{b.rowsOk}</td>
                  <td className={b.rowsError ? 'text-destructive py-2' : 'py-2'}>{b.rowsError}</td>
                  <td className="py-2">{b.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted-foreground text-sm">Aucun import pour l'instant.</p>
        )}
      </section>
    </div>
  );
}
